import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  Fragment
} from 'react';
import DataTable from 'react-data-table-component';
import Notification from 'components/Notification';
import { uploadFile } from 'utils/network/ServiceHelper';
import { Transition, Switch, Listbox } from '@headlessui/react';
import MultiSelect from 'components/MultiSelect';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import {
  ANDROID_LOWERCASE,
  STATUS_NULL,
  PAGINATION,
  STATUS_UNAUTHORIZED,
  SUCCESS_MESSAGE,
  WEB_LOWERCASE,
  STATUS_SUCCESS
} from 'utils/constants';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import segmentService from 'utils/network/segment/segment.service';
import { useNotif } from 'context/NotificationContext';
import {
  TYPES,
  DELIMITER_TYPES,
  SOURCE_OPTIONS
} from 'utils/constants/Segment';

export default function Segment() {
  const navigate = useNavigate();
  const { notification, setNotification } = useNotif();
  const columns = [
    {
      name: 'Name',
      sortable: true,
      selector: 'name',
      cell: function render(row) {
        return <>{row.name}</>;
      }
    },
    {
      name: 'Description',
      cell: function render(row) {
        return <>{row.description}</>;
      }
    },
    {
      name: 'Status',
      cell: function render(row) {
        return (
          <>
            <span
              className={cn({
                'text-red-400': row.status === 'failed',
                'text-green-400': row.status === 'success'
              })}
            >
              {row.status}
            </span>
          </>
        );
      }
    },
    {
      name: 'Source',
      cell: function render(row) {
        return <>{row.source}</>;
      }
    },
    {
      name: 'Notification',
      cell: function render(row) {
        return (
          <>
            {row.is_notification ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
          </>
        );
      }
    },
    {
      name: 'Target Type',
      cell: function render(row) {
        return (
          <>
            {row.target_type.map((x, index) => {
              if (x === ANDROID_LOWERCASE) {
                return (
                  <svg
                    key={index}
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5 mr-3"
                    x="0"
                    y="0"
                    fill="#3CDB85"
                    enableBackground="new 0 0 553.048 553.048"
                    version="1.1"
                    viewBox="0 0 553.048 553.048"
                    xmlSpace="preserve"
                  >
                    <path d="M76.774 179.141c-9.529 0-17.614 3.323-24.26 9.969-6.646 6.646-9.97 14.621-9.97 23.929v142.914c0 9.541 3.323 17.619 9.97 24.266 6.646 6.646 14.731 9.97 24.26 9.97 9.522 0 17.558-3.323 24.101-9.97 6.53-6.646 9.804-14.725 9.804-24.266V213.039c0-9.309-3.323-17.283-9.97-23.929-6.647-6.646-14.627-9.969-23.935-9.969zM351.972 50.847L375.57 7.315c1.549-2.882.998-5.092-1.658-6.646-2.883-1.34-5.098-.661-6.646 1.989l-23.928 43.88c-21.055-9.309-43.324-13.972-66.807-13.972-23.488 0-45.759 4.664-66.806 13.972l-23.929-43.88c-1.555-2.65-3.77-3.323-6.646-1.989-2.662 1.561-3.213 3.764-1.658 6.646l23.599 43.532c-23.929 12.203-42.987 29.198-57.167 51.022-14.18 21.836-21.273 45.698-21.273 71.628h307.426c0-25.924-7.094-49.787-21.273-71.628-14.181-21.824-33.129-38.819-56.832-51.022zm-136.433 63.318c-2.552 2.558-5.6 3.831-9.143 3.831-3.55 0-6.536-1.273-8.972-3.831-2.436-2.546-3.654-5.582-3.654-9.137 0-3.543 1.218-6.585 3.654-9.137 2.436-2.546 5.429-3.819 8.972-3.819s6.591 1.273 9.143 3.819c2.546 2.558 3.825 5.594 3.825 9.137-.007 3.549-1.285 6.591-3.825 9.137zm140.086 0c-2.441 2.558-5.434 3.831-8.971 3.831-3.551 0-6.598-1.273-9.145-3.831-2.551-2.546-3.824-5.582-3.824-9.137 0-3.543 1.273-6.585 3.824-9.137 2.547-2.546 5.594-3.819 9.145-3.819 3.543 0 6.529 1.273 8.971 3.819 2.438 2.558 3.654 5.594 3.654 9.137 0 3.549-1.217 6.591-3.654 9.137zM123.971 406.804c0 10.202 3.543 18.838 10.63 25.925 7.093 7.087 15.729 10.63 25.924 10.63h24.596l.337 75.454c0 9.528 3.323 17.619 9.969 24.266s14.627 9.97 23.929 9.97c9.523 0 17.613-3.323 24.26-9.97s9.97-14.737 9.97-24.266v-75.447h45.864v75.447c0 9.528 3.322 17.619 9.969 24.266s14.73 9.97 24.26 9.97c9.523 0 17.613-3.323 24.26-9.97s9.969-14.737 9.969-24.266v-75.447h24.928c9.969 0 18.494-3.544 25.594-10.631 7.086-7.087 10.631-15.723 10.631-25.924V185.45h-305.09v221.354zM476.275 179.141c-9.309 0-17.283 3.274-23.93 9.804-6.646 6.542-9.969 14.578-9.969 24.094v142.914c0 9.541 3.322 17.619 9.969 24.266s14.627 9.97 23.93 9.97c9.523 0 17.613-3.323 24.26-9.97s9.969-14.725 9.969-24.266V213.039c0-9.517-3.322-17.552-9.969-24.094-6.647-6.53-14.737-9.804-24.26-9.804z"></path>
                  </svg>
                );
              }
              if (x === WEB_LOWERCASE) {
                return (
                  <svg
                    key={index}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5 mr-3"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                    />
                  </svg>
                );
              }

              return (
                <svg
                  key={index}
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 mr-3"
                  x="0"
                  y="0"
                  enableBackground="new 0 0 512 512"
                  version="1.1"
                  viewBox="0 0 512 512"
                  xmlSpace="preserve"
                >
                  <path d="M185.255 512c-76.201-.439-139.233-155.991-139.233-235.21 0-129.404 97.075-157.734 134.487-157.734 16.86 0 34.863 6.621 50.742 12.48 11.104 4.087 22.588 8.306 28.975 8.306 3.823 0 12.832-3.589 20.786-6.738 16.963-6.753 38.071-15.146 62.651-15.146h.146c18.354 0 74.004 4.028 107.461 54.272l7.837 11.777-11.279 8.511c-16.113 12.158-45.513 34.336-45.513 78.267 0 52.031 33.296 72.041 49.292 81.665 7.061 4.248 14.37 8.628 14.37 18.208 0 6.255-49.922 140.566-122.417 140.566-17.739 0-30.278-5.332-41.338-10.034-11.191-4.761-20.845-8.862-36.797-8.862-8.086 0-18.311 3.823-29.136 7.881C221.496 505.73 204.752 512 185.753 512h-.498zM351.343 0c1.888 68.076-46.797 115.304-95.425 112.342C247.905 58.015 304.54 0 351.343 0z"></path>
                </svg>
              );
            })}
          </>
        );
      }
    },
    {
      name: 'Actions',
      cell: function render(row) {
        return (
          <div className="w-full">
            <button
              className="p-1 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleShowDetail(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </button>
            <button
              className="p-1 ml-2 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleCreateEdit(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              className="p-1 ml-2 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleDelete(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        );
      }
    }
  ];

  const [pagination, setPagination] = useState({
    search: PAGINATION.DEFAULT_KEYWORD,
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
    totalPage: PAGINATION.DEFAULT_TOTAL_PAGE,
    totalData: PAGINATION.DEFAULT_TOTAL_DATA
  });
  const [form, setForm] = useState({
    id: null,
    name: '',
    description: '',
    source: '',
    target_type: [],
    delimiter: DELIMITER_TYPES[0],
    is_notification: true,
    file_name: '',
    file_type: '',
    file_link: ''
  });
  const [createEdit, setCreateEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [source, setSource] = useState('');
  const [sortProp, setSortProp] = useState({
    order: 'created_at',
    sort: 'desc'
  });

  const query = useQueryClient();
  const queryKey = useMemo(() => {
    return `segment-list--${JSON.stringify({
      page: pagination.page,
      perPage: pagination.perPage,
      search: pagination.search,
      sortProp,
      source
    })}`;
  }, [
    pagination.page,
    pagination.perPage,
    pagination.search,
    sortProp,
    source
  ]);

  // get list
  const { data, isLoading: getSegmentLoading } = useQuery({
    queryKey,
    queryFn: () => {
      return segmentService.list({
        limit: pagination.perPage,
        keyword: pagination.search,
        offset: pagination.page,
        order: sortProp.order,
        sort: sortProp.sort,
        source
      });
    },
    onSuccess: (data) => {
      if (data?.data?.data === STATUS_NULL) {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: data?.data?.error_message
        });
      }
    }
  });

  // create or update
  const { mutate: createUpdateSegment } = useMutation(
    (body) => {
      if (body.id) {
        return segmentService.put(body);
      }
      return segmentService.post(body);
    },
    {
      onSuccess: (data) => {
        if (data?.status === STATUS_SUCCESS) {
          setNotification({
            ...notification,
            show: true,
            success: true,
            title: SUCCESS_MESSAGE,
            message: `Segment successfully ${form?.id ? 'updated' : 'created'}`
          });
          query.invalidateQueries([queryKey]);
          closeCreateEdit();
        }
      },
      onError: () => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: 'Error create segment'
        });
      }
    }
  );

  // delete
  const { mutate: deleteSegment } = useMutation(
    (id) => {
      return segmentService.delete(id);
    },
    {
      onSuccess: (data) => {
        if (data?.status === STATUS_SUCCESS) {
          query.invalidateQueries([queryKey]);
          closeDelete();
          setNotification({
            ...notification,
            success: true,
            show: true,
            title: SUCCESS_MESSAGE,
            message: 'Segment successfully deleted'
          });
        }
      },
      onError: () => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: 'Error delete segment'
        });
      }
    }
  );

  const handlePerRowsChange = useCallback(
    (newPerPage, page) => {
      setPagination({
        ...pagination,
        page,
        perPage: newPerPage,
        totalPage: Math.ceil((data?.data?.total_data || 0) / newPerPage)
      });
    },
    [pagination, data]
  );

  function handlePageChange(page) {
    setPagination({
      ...pagination,
      page
    });
  }

  const handleCreateEdit = (row) => () => {
    if (row) {
      const targetType = [];
      row.target_type.forEach((x) => {
        TYPES.forEach((v) => {
          if (v.value === x) {
            targetType.push({
              ...v,
              selected: true
            });
          }
        });
      });

      setForm({
        ...form,
        id: row.id,
        name: row.name,
        description: row.description,
        is_notification: row.is_notification,
        target_type: targetType,
        source: row.source,
        delimiter: row.delimiter,
        file_name: row.file_name,
        file_type: row.file_type,
        file_link: row.file_link
      });
    }

    setCreateEdit(true);
  };

  function closeCreateEdit() {
    setCreateEdit(false);
  }

  const handleDelete = (row) => () => {
    setForm({ ...form, id: row.id });
    setShowDelete(true);
  };

  function closeDelete() {
    setShowDelete(false);
  }

  const handleShowDetail = (row) => () => {
    if (row) {
      const targetType = [];
      row.target_type.forEach((x) => {
        TYPES.forEach((v) => {
          if (v.value === x) {
            targetType.push(v);
          }
        });
      });

      setForm({
        ...form,
        id: row.id,
        name: row.name,
        description: row.description,
        is_notification: row.is_notification,
        source: row.source,
        target_type: targetType,
        delimiter: row.delimiter,
        file_name: row.file_name,
        file_type: row.file_type,
        file_link: row.file_link
      });
    }

    setShowDetail(true);
  };

  function closeShowDetail() {
    setShowDetail(false);
  }

  function handleChangeForm(e) {
    if (e.target.name === 'description' && e.target.value.length > 200) return;
    if (e.target.name === 'file') {
      setForm({
        ...form,
        file: e.target.files[0],
        delimiter: DELIMITER_TYPES[0]
      });

      return;
    }

    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  }

  function handleDelimiterTypeChange(value) {
    setForm({
      ...form,
      delimiter: DELIMITER_TYPES.find((e) => e.key === value)
    });
  }

  function handleChangeNotification(value) {
    setForm({ ...form, is_notification: value });
  }

  function notif(title, message, isError = false, duration = 3000) {
    setNotification({
      show: true,
      title: title,
      message: message,
      error: isError
    });

    setTimeout(() => {
      setNotification({ ...notification, show: false });
    }, duration);
  }

  function closeNotification() {
    setNotification({ ...notification, show: false });
  }

  const handleSort = useCallback((column, sortDirection) => {
    setSortProp({
      order: column.selector,
      sort: sortDirection
    });
  });

  function handleCopyLink() {
    const splitted = form.file_link.split('/');
    const filename = splitted[splitted.length - 1];
    const a = document.createElement('a');
    a.setAttribute('href', form.file_link);
    a.setAttribute('download', filename);
    a.click();
  }

  function handleDownloadCsv() {
    const appUrl = process.env.REACT_APP_APP_URL;
    window.open(`${appUrl}/segment_target_template.csv`);
  }

  // Is image optional?
  function isValid() {
    if (form.id) {
      return (
        form.name.length > 0 &&
        form.description.length > 0 &&
        form.target_type.length > 0
      );
    }

    return (
      form.name.length > 0 &&
      form.description.length > 0 &&
      form.target_type.length > 0 &&
      form.file !== undefined &&
      form.file !== null
    );
  }

  useEffect(() => {
    if (!createEdit && !showDelete)
      setForm({
        ...form,
        id: null,
        name: '',
        description: '',
        target_type: [],
        delimiter: DELIMITER_TYPES[0],
        is_notification: true,
        file: null,
        file_name: '',
        file_type: '',
        file_link: ''
      });
  }, [createEdit, showDelete]);

  function handleSourceChange(value) {
    setForm({
      ...form,
      source: value
    });
  }

  async function handleCreateEditSegment() {
    if (!isValid()) {
      notif('Error', 'Please fill all required field!', true);
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;
      let fileUrl = form.file_link,
        fileName = form.file_name,
        fileType = form.file_type;

      if (form.file) {
        const request = new FormData();
        request.append('file', form.file, form.file.name);
        const res = await uploadFile(baseUrl, request, 'csv');

        if (res?.status === STATUS_SUCCESS) {
          const { data } = res;
          fileUrl = data.url;
          fileName = data.filename;
          fileType = 'csv';
        } else {
          // handle upload error
          if (res?.status === STATUS_UNAUTHORIZED) {
            localStorage.clear();
            navigate('/login', { replace: true });
            return;
          }

          const error = JSON.parse(await res.text());
          if (error) {
            notif(
              'Error',
              error.message ?? 'Network error. Please try again later!',
              true
            );
            return;
          }
        }
      }

      form.target_type = form.target_type.map((x) => x.value);
      form.delimiter = form.delimiter.key;
      form.file_name = fileName;
      form.file_link = fileUrl;
      form.file_type = fileType;

      createUpdateSegment(form);
    } catch (error) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  async function handleDeleteSegment() {
    deleteSegment(form.id);
  }

  useEffect(() => {
    const column3 = document.getElementById('column-3');
    if (column3) {
      column3.title = 'Upload & Parsing CSV Status';
      column3.classList.add('cursor-pointer');
    }
  });

  const searchDebounce = useRef();

  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
          Segment
        </h2>
        <div className="flex flex-row">
          <button
            className="px-4 py-2 mt-3 mr-3 text-sm font-medium text-center bg-white border border-yellow-500 rounded-lg text-secondary leading-5 text-purple transition-colors duration-150 active:bg-yellow-600 hover:bg-yellow-100 focus:outline-none focus:shadow-outline-purple"
            onClick={handleDownloadCsv}
          >
            Download CSV Template
          </button>
          <button
            className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
            onClick={handleCreateEdit(null)}
          >
            Add New
          </button>
        </div>
      </div>
      <div className="flex flex-row items-center gap-3 pb-3">
        <div className="relative">
          <input
            className="block w-full px-3 py-2 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
            placeholder="John Doe"
            name="search"
            onChange={({ target: { value } }) => {
              if (searchDebounce.current) {
                clearTimeout(searchDebounce.current);
              }

              searchDebounce.current = setTimeout(() => {
                setPagination({ ...pagination, search: value });
              }, 800);
            }}
          />
          <div className="absolute top-0 bottom-0 flex flex-row items-center h-full right-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
        <div>
          <MultiSelect
            data={SOURCE_OPTIONS}
            onChange={(value) =>
              setSource(value.map((item) => item.value).join(','))
            }
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        responsive
        sortServer
        onSort={handleSort}
        progressPending={getSegmentLoading}
        pagination
        paginationServer
        paginationTotalRows={data?.data?.total_data}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
      />

      {/* Create Edit Section */}
      <Transition
        show={createEdit}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block px-8 pt-5 pb-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              {form.id ? (
                <h3 className="mb-3 font-bold">Edit Segment</h3>
              ) : (
                <h3 className="mb-3 font-bold">Create Segment</h3>
              )}
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeCreateEdit}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Device Type <span className="text-red-500">*</span>
                </span>
                {createEdit ? (
                  form.id ? (
                    <MultiSelect
                      data={TYPES}
                      onChange={(value) =>
                        setForm({ ...form, target_type: value })
                      }
                      currentValue={form.target_type}
                    />
                  ) : (
                    <MultiSelect
                      data={TYPES}
                      onChange={(value) =>
                        setForm({ ...form, target_type: value })
                      }
                    />
                  )
                ) : null}
              </label>
              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Source <span className="text-red-500">*</span>
                </span>
                <Listbox value={form.source} onChange={handleSourceChange}>
                  <div className="relative mt-1">
                    <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left bg-white border rounded-lg cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm">
                      <span className="block truncate">
                        {form.source ? form.source : SOURCE_OPTIONS[0].value}
                      </span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                          />
                        </svg>
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white shadow-lg rounded-md max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {SOURCE_OPTIONS.map((device, index) => (
                          <Listbox.Option
                            key={index}
                            className={({ active }) =>
                              `${
                                active
                                  ? 'text-amber-900 bg-amber-100'
                                  : 'text-gray-900'
                              }
                                   cursor-default select-none relative py-2 pl-10 pr-4`
                            }
                            value={device.value}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={`${
                                    selected ? 'font-medium' : 'font-normal'
                                  } block truncate`}
                                >
                                  {device.label}
                                </span>
                                {selected ? (
                                  <span
                                    className={`${
                                      active
                                        ? 'text-amber-600'
                                        : 'text-amber-600'
                                    }
                                         absolute inset-y-0 left-0 flex items-center pl-3`}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="w-5 h-5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </label>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Name <span className="text-red-500">*</span>
                </span>
                <input
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="National Shopping Day"
                  name="name"
                  value={form.name}
                  onChange={handleChangeForm}
                />
              </label>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Description <span className="text-red-500">*</span>
                </span>
                <textarea
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none h-28 rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="Donec faucibus felis facilisi potenti tempus sed dictumst, proin nulla libero ex facilisis class, maecenas fermentum magnis ridiculus id sagittis."
                  name="description"
                  value={form.description}
                  onChange={handleChangeForm}
                />
              </label>
              <label className="block mt-3 text-sm">
                <div className="flex flex-col">
                  <span className="text-gray-700 dark:text-gray-400">
                    Include Notification
                  </span>
                  <span className="text-xs text-gray-400">
                    If enabled, it will send notification to the devices
                  </span>
                </div>
                <div className="mt-2">
                  <Switch
                    checked={form.is_notification}
                    onChange={handleChangeNotification}
                    className={cn(
                      'relative inline-flex flex-shrink-0 w-10 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75',
                      {
                        'bg-gray-500': form.is_notification,
                        'bg-gray-200': !form.is_notification
                      }
                    )}
                  >
                    <span className="sr-only">Enable notifications</span>
                    <span
                      aria-hidden="true"
                      className={cn(
                        'pointer-events-none inline-block w-4 h-4 rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200',
                        {
                          'translate-x-5': form.is_notification,
                          'translate-x-0': !form.is_notification
                        }
                      )}
                    />
                  </Switch>
                </div>
              </label>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  File <span className="text-red-500">*</span>
                </span>
                {form.id ? (
                  <div className="mt-3 grid grid-cols-3">
                    <div>File Name</div>
                    <div className="text-gray-500 col-span-2">
                      {form.file_name}
                    </div>
                    <div>File Link</div>
                    <div className="text-gray-500 col-span-2">
                      {form.file_link}
                    </div>
                  </div>
                ) : null}
                <input
                  readOnly
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="Upload File"
                  type="file"
                  name="file"
                  onChange={handleChangeForm}
                />
              </label>
              {form.file && (
                <label className="block mt-3 text-sm">
                  <span className="text-gray-700 dark:text-gray-400">
                    Type Delimiter <span className="text-red-500">*</span>
                  </span>
                  <Listbox
                    value={form.delimiter}
                    onChange={handleDelimiterTypeChange}
                  >
                    <div className="relative mt-1">
                      <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left bg-white border rounded-lg cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm">
                        <span className="block truncate">
                          {form.delimiter?.title}
                        </span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                            />
                          </svg>
                        </span>
                      </Listbox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white shadow-lg rounded-md max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                          {DELIMITER_TYPES.map((type, index) => (
                            <Listbox.Option
                              key={index}
                              className={({ active }) =>
                                cn(
                                  'cursor-default select-none relative py-2 pl-10 pr-4',
                                  {
                                    'text-amber-900 bg-amber-100': active,
                                    'text-gray-900': !active
                                  }
                                )
                              }
                              value={type.key}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span
                                    className={cn('block truncate', {
                                      'font-medium': selected,
                                      'font-normal': !selected
                                    })}
                                  >
                                    {type.title}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={cn(
                                        'absolute inset-y-0 left-0 flex items-center pl-3',
                                        {
                                          'text-amber-600': active
                                        }
                                      )}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </Listbox>
                </label>
              )}
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleCreateEditSegment}
                >
                  {form.id ? 'Edit' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      {/* Delete Section */}
      <Transition
        show={showDelete}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Delete Confirmation</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeDelete}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p>Are you sure to delete this item?</p>
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={closeDelete}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center border border-transparent rounded-lg leading-5 transition-colors duration-150 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleDeleteSegment}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <Transition
        show={showDetail}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Detail {form.name ? form.name : ''}</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeShowDetail}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="mt-5 grid grid-cols-2">
                <div className="py-2 font-semibold">Name</div>
                <div className="py-2 pl-4">{form.name ? form.name : ''}</div>
                <div className="py-2 font-semibold">Description</div>
                <div className="py-2 pl-4">
                  {form.description ? form.description : ''}
                </div>
                <div className="py-2 font-semibold">Target Type</div>
                <div className="py-2 pl-4">
                  <div className="flex flex-row">
                    {form.target_type
                      ? form.target_type.map((x, index) => {
                          if (x.value === ANDROID_LOWERCASE) {
                            return (
                              <svg
                                key={index}
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 mr-3"
                                x="0"
                                y="0"
                                fill="#3CDB85"
                                enableBackground="new 0 0 553.048 553.048"
                                version="1.1"
                                viewBox="0 0 553.048 553.048"
                                xmlSpace="preserve"
                              >
                                <path d="M76.774 179.141c-9.529 0-17.614 3.323-24.26 9.969-6.646 6.646-9.97 14.621-9.97 23.929v142.914c0 9.541 3.323 17.619 9.97 24.266 6.646 6.646 14.731 9.97 24.26 9.97 9.522 0 17.558-3.323 24.101-9.97 6.53-6.646 9.804-14.725 9.804-24.266V213.039c0-9.309-3.323-17.283-9.97-23.929-6.647-6.646-14.627-9.969-23.935-9.969zM351.972 50.847L375.57 7.315c1.549-2.882.998-5.092-1.658-6.646-2.883-1.34-5.098-.661-6.646 1.989l-23.928 43.88c-21.055-9.309-43.324-13.972-66.807-13.972-23.488 0-45.759 4.664-66.806 13.972l-23.929-43.88c-1.555-2.65-3.77-3.323-6.646-1.989-2.662 1.561-3.213 3.764-1.658 6.646l23.599 43.532c-23.929 12.203-42.987 29.198-57.167 51.022-14.18 21.836-21.273 45.698-21.273 71.628h307.426c0-25.924-7.094-49.787-21.273-71.628-14.181-21.824-33.129-38.819-56.832-51.022zm-136.433 63.318c-2.552 2.558-5.6 3.831-9.143 3.831-3.55 0-6.536-1.273-8.972-3.831-2.436-2.546-3.654-5.582-3.654-9.137 0-3.543 1.218-6.585 3.654-9.137 2.436-2.546 5.429-3.819 8.972-3.819s6.591 1.273 9.143 3.819c2.546 2.558 3.825 5.594 3.825 9.137-.007 3.549-1.285 6.591-3.825 9.137zm140.086 0c-2.441 2.558-5.434 3.831-8.971 3.831-3.551 0-6.598-1.273-9.145-3.831-2.551-2.546-3.824-5.582-3.824-9.137 0-3.543 1.273-6.585 3.824-9.137 2.547-2.546 5.594-3.819 9.145-3.819 3.543 0 6.529 1.273 8.971 3.819 2.438 2.558 3.654 5.594 3.654 9.137 0 3.549-1.217 6.591-3.654 9.137zM123.971 406.804c0 10.202 3.543 18.838 10.63 25.925 7.093 7.087 15.729 10.63 25.924 10.63h24.596l.337 75.454c0 9.528 3.323 17.619 9.969 24.266s14.627 9.97 23.929 9.97c9.523 0 17.613-3.323 24.26-9.97s9.97-14.737 9.97-24.266v-75.447h45.864v75.447c0 9.528 3.322 17.619 9.969 24.266s14.73 9.97 24.26 9.97c9.523 0 17.613-3.323 24.26-9.97s9.969-14.737 9.969-24.266v-75.447h24.928c9.969 0 18.494-3.544 25.594-10.631 7.086-7.087 10.631-15.723 10.631-25.924V185.45h-305.09v221.354zM476.275 179.141c-9.309 0-17.283 3.274-23.93 9.804-6.646 6.542-9.969 14.578-9.969 24.094v142.914c0 9.541 3.322 17.619 9.969 24.266s14.627 9.97 23.93 9.97c9.523 0 17.613-3.323 24.26-9.97s9.969-14.725 9.969-24.266V213.039c0-9.517-3.322-17.552-9.969-24.094-6.647-6.53-14.737-9.804-24.26-9.804z"></path>
                              </svg>
                            );
                          }

                          if (x.value === 'ios') {
                            return (
                              <svg
                                key={index}
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 mr-3"
                                x="0"
                                y="0"
                                enableBackground="new 0 0 512 512"
                                version="1.1"
                                viewBox="0 0 512 512"
                                xmlSpace="preserve"
                              >
                                <path d="M185.255 512c-76.201-.439-139.233-155.991-139.233-235.21 0-129.404 97.075-157.734 134.487-157.734 16.86 0 34.863 6.621 50.742 12.48 11.104 4.087 22.588 8.306 28.975 8.306 3.823 0 12.832-3.589 20.786-6.738 16.963-6.753 38.071-15.146 62.651-15.146h.146c18.354 0 74.004 4.028 107.461 54.272l7.837 11.777-11.279 8.511c-16.113 12.158-45.513 34.336-45.513 78.267 0 52.031 33.296 72.041 49.292 81.665 7.061 4.248 14.37 8.628 14.37 18.208 0 6.255-49.922 140.566-122.417 140.566-17.739 0-30.278-5.332-41.338-10.034-11.191-4.761-20.845-8.862-36.797-8.862-8.086 0-18.311 3.823-29.136 7.881C221.496 505.73 204.752 512 185.753 512h-.498zM351.343 0c1.888 68.076-46.797 115.304-95.425 112.342C247.905 58.015 304.54 0 351.343 0z"></path>
                              </svg>
                            );
                          }

                          return (
                            <svg
                              key={index}
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-5 h-5 mr-3"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                              />
                            </svg>
                          );
                        })
                      : ''}
                  </div>
                </div>
                <div className="font-semibold">File Name</div>
                <div className="py-2 pl-4">
                  {form.file_name ? form.file_name : ''}
                </div>
                <div className="font-semibold">File Type</div>
                <div className="py-2 pl-4">
                  {form.file_type ? form.file_type : ''}
                </div>
                <div className="font-semibold">File Link</div>
                <div className="py-2 pl-4">
                  {form.file_link ? (
                    <div className="relative">
                      <input
                        type="text"
                        id="copy-text"
                        value={form.file_link}
                        className="absolute opacity-0"
                      />
                      <button
                        className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                        onClick={handleCopyLink}
                      >
                        Download File
                      </button>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
                <div className="font-semibold">Segment Target</div>
                <div className="py-2 pl-4">
                  <Link
                    className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                    to={`/segment-target?id=${form.id}&name=${form.name}&source=${form.source}`}
                  >
                    Segment Target List
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <Notification
        notification={notification}
        closeNotification={closeNotification}
      />
    </div>
  );
}
